import * as React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { loginStatusVar } from '@context';
import SignupForm from './signup-form';

interface SignupDialogProps {
  type?: 'consumer' | 'spot' | 'artist';
  incentiveText?: string | null;
  trigger?: React.ReactElement<ButtonProps>;
  redirect?: string | null;
}

export function SignupDialog(props: Readonly<SignupDialogProps>) {
  const { t } = useTranslation('authentication');
  const {
    type: initialType = 'consumer',
    trigger,
    incentiveText = t('Register now, book your favorite spots and discover new ones!'),
    redirect = null,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [type, setType] = React.useState<'consumer' | 'spot' | 'artist'>(initialType);
  const loginStatus = useReactiveVar(loginStatusVar);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleBack = () => {
    setType(initialType);
  };

  return (
    <React.Fragment>
      {trigger ? (
        React.cloneElement(trigger, {
          disabled: loginStatus.isLoggedIn,
          loading: loginStatus.isLogging,
          onClick: handleClickOpen,
        })
      ) : (
        <Stack width="100%" spacing={1} direction="row" justifyContent="flex-start">
          <Typography variant="body2" textAlign="left" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
            {t('No account yet?')}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            fontSize={{ xs: '.7rem', sm: '.8rem' }}
            onClick={handleClickOpen}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
                color: 'text.primary',
              },
            }}
          >
            {t('signup', { ns: 'cta' })}
          </Typography>
        </Stack>
      )}

      <Dialog
        open={open}
        fullScreen
        onClose={handleClose}
        aria-describedby="singup-dialog-slide-description"
        sx={{ maxHeight: '100vh' }}
      >
        <Stack direction="row" p={3} justifyContent="space-between">
          <Typography variant="h5" fontWeight="bold">
            {t('Registration')}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            sx={{ color: 'grey' }}
            onClick={handleClose}
          >
            {t('close', { ns: 'cta' })}
          </Button>
        </Stack>
        <Stack spacing={6} p={3} height="100%">
          <Typography textAlign="center" color="textSecondary">
            {incentiveText}
          </Typography>
          <SignupForm type={type} handleBack={handleBack} redirect={redirect} />
        </Stack>
      </Dialog>
    </React.Fragment>
  );
}
